import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { fetchStats } from "@/lib/api";
import { SimTag, CountUp, CommunitySection } from "@/components/shared";
import BasketVisual from "@/components/BasketVisual";
import MechanismFlow from "@/components/MechanismFlow";
import VoteSection from "@/components/VoteSection";

const MARQUEE_ITEMS = [
  "AUTOMATED SOLANA BASKET ENGINE",
  "100% ON-CHAIN TRANSPARENT AUDIT",
  "NO LOCKUPS REQUIRED",
  "JUPITER ROUTED SWAPS (PRE-LAUNCH ARCHITECTURE)",
  "COMMUNITY REWARD ALLOCATION",
  "$BASK ECOSYSTEM POOL",
];

export default function Home() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchStats().then(setStats).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <header className="relative isolate overflow-hidden pt-20 pb-28 px-6 bg-[#07050d]">
        <div className="absolute inset-0 sol-glow pointer-events-none" />
        <div className="max-w-6xl mx-auto relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#9945FF]/10 border border-[#9945FF]/30 text-xs text-[#14F195] mono-font mb-6 d1-up">
              <span className="w-2 h-2 rounded-full bg-[#14F195] animate-ping" />
              Solana Meme-Coin Automated Asset Basket
            </div>

            <h1 className="display-font uppercase text-white mb-6 leading-[0.95]" style={{ fontSize: "clamp(40px, 5.5vw, 84px)" }} data-testid="hero-title">
              <span className="ln"><span>One Token.</span></span>
              <span className="ln"><span className="text-transparent bg-clip-text bg-gradient-to-r from-[#9945FF] via-[#00F0FF] to-[#14F195]">An Entire Basket.</span></span>
            </h1>

            <p className="text-lg md:text-xl text-[#b5aed0] max-w-xl leading-relaxed mb-8 d1-up d1-delay-1" data-testid="hero-subtitle">
              Unlike ordinary meme coins that rely purely on hype, $BASK turns a portion of project fees into a growing basket of curated Solana assets — allocated proportionally to eligible holders, tracked transparently by everyone.
            </p>

            <div className="flex flex-wrap items-center gap-4 d1-up d1-delay-2">
              <Link to="/dashboard" data-testid="hero-explore-basket-btn"
                className="px-7 py-3.5 rounded-xl bg-gradient-to-r from-[#9945FF] to-[#14F195] text-black font-bold text-sm tracking-wide uppercase hover:shadow-[0_0_25px_rgba(20,241,149,0.4)] transition-shadow flex items-center gap-2">
                Explore The Basket <ArrowRight size={16} />
              </Link>
              <Link to="/how-it-works" data-testid="hero-how-it-works-btn"
                className="px-7 py-3.5 rounded-xl glass-panel text-white font-medium text-sm hover:border-[#14F195]/60 transition-colors">
                How It Works
              </Link>
            </div>
          </div>

          {/* Metrics card */}
          <div className="lg:col-span-4 d1-up d1-delay-2">
            <div className="glass-panel rounded-2xl p-6 relative overflow-hidden border border-[#9945FF]/30 shadow-2xl" data-testid="hero-metrics-card">
              <div className="flex items-center justify-between pb-4 border-b border-white/10">
                <span className="mono-font text-xs text-[#9b93b8]">PROTOCOL METRICS</span>
                <SimTag />
              </div>
              <div className="mt-6 space-y-5">
                <div>
                  <div className="text-xs text-[#9b93b8] uppercase tracking-wider mb-1">Simulated Treasury Value</div>
                  <div className="text-3xl font-bold text-white mono-font flex items-baseline flex-wrap" data-testid="hero-treasury-value">
                    <CountUp value={stats?.treasury_value_usd ?? 248950} prefix="$" />
                    <span className="text-xs text-[#14F195] ml-2 font-normal">+{stats?.treasury_change_30d_pct ?? 18.4}% (demo)</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div className="p-3 rounded-lg bg-black/40 border border-white/5">
                    <span className="text-[11px] text-[#9b93b8] block">Fee Inflow</span>
                    <span className="text-base font-semibold text-white mono-font">{stats?.fee_percent ?? 4}% Pool</span>
                  </div>
                  <div className="p-3 rounded-lg bg-black/40 border border-white/5">
                    <span className="text-[11px] text-[#9b93b8] block">Curated Tokens</span>
                    <span className="text-base font-semibold text-[#14F195] mono-font">{stats?.assets_count ?? 5} Assets</span>
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-[#9945FF]/10 border border-[#9945FF]/30 text-xs text-[#d3cbef]">
                  Simulated real-time automated buy executions deployed across decentralized Solana AMMs.
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* MARQUEE */}
      <div className="mq-band py-3 bg-[#0d091a] border-y border-[#9945FF]/20 text-xs mono-font text-[#14F195]">
        <div className="mq items-center gap-10">
          {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
            <span key={i}>• {item}</span>
          ))}
        </div>
      </div>

      {/* VISUAL BASKET */}
      <BasketVisual />

      {/* HOW IT WORKS — animated 3-step flow */}
      <MechanismFlow />

      {/* ECOSYSTEM STATS */}<section className="py-24 px-6 bg-[#07050d] border-b border-white/5 relative overflow-hidden">
        <div className="absolute inset-0 sol-glow pointer-events-none opacity-50" />
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="flex items-center gap-3 justify-center mb-4">
            <span className="mono-font text-xs uppercase tracking-widest text-[#14F195] font-semibold">Ecosystem Pulse</span>
            <SimTag>Example Statistics</SimTag>
          </div>
          <h2 className="display-font uppercase text-3xl md:text-5xl text-white text-center mb-14">A Vault That Never Sleeps</h2>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6" data-testid="ecosystem-stats">
            {[
              { label: "Treasury Value", value: stats?.treasury_value_usd ?? 248950, prefix: "$", testid: "stat-treasury" },
              { label: "Fees Collected (24h)", value: stats?.fees_collected_24h_usd_demo ?? 3214.55, prefix: "$", decimals: 2, testid: "stat-fees" },
              { label: "Distributed To Date", value: stats?.distributed_to_date_usd_demo ?? 41280.75, prefix: "$", decimals: 2, testid: "stat-distributed" },
              { label: "Example Holders", value: stats?.holders_demo ?? 1420, testid: "stat-holders" },
            ].map((s) => (
              <div key={s.label} data-testid={s.testid} className="glass-panel rounded-2xl p-6 text-center hover:border-[#14F195]/40 transition-colors">
                <div className="text-2xl md:text-3xl font-bold text-white mono-font">
                  <CountUp value={s.value} prefix={s.prefix ?? ""} decimals={s.decimals ?? 0} />
                </div>
                <div className="text-[11px] text-[#9b93b8] uppercase tracking-wider mt-2">{s.label}</div>
              </div>
            ))}
          </div>

          <p className="text-center text-[11px] text-[#7a7493] mono-font mt-8">
            All figures above are example data for the interactive prototype — no real assets are held yet.
          </p>
        </div>
      </section>

      {/* COMMUNITY VOTING */}
      <VoteSection />

      <CommunitySection />
    </div>
  );
}
